import { useState } from 'react';
import { androidIntentUrl, inAppBrowser, inAppName, isAndroid, isIOS, safariUrl } from '@/lib/inapp';
import { landingBeacon } from '@/lib/beacon';

/**
 * Shown wherever someone could sign in, when the page is open inside an
 * app's own browser (Instagram, Facebook, Snapchat…): Google refuses to sign
 * anyone in there, so without this the Google button just fails. We hand the
 * page to the real browser instead.
 */
export function OpenInBrowser({ tone = 'night', title = 'Open Popin in your browser to sign up', why }: { tone?: 'night' | 'bone'; title?: string; why?: string }) {
  const [copied, setCopied] = useState(false);
  const app = inAppBrowser();
  if (!app) return null;
  const where = inAppName(app);
  const href = typeof window === 'undefined' ? '' : window.location.href;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const night = tone === 'night';
  const btn = night ? 'bg-bone text-night' : 'bg-night text-bone';
  const ghost = night ? 'bg-bone/15' : 'bg-night/10';
  return (
    <div className={night ? 'rounded-card bg-night p-4 text-bone' : 'rounded-card bg-bone p-4 text-night'} role="note" data-testid="open-in-browser">
      <p className="text-[17px] font-semibold">{title}</p>
      <p className={night ? 'mt-1 text-[15px] text-bone/80' : 'mt-1 text-[15px] text-night/75'}>
        {why ?? `Google sign-in doesn’t work inside ${where}.`}{' '}
        {isAndroid() ? 'Tap below, or use ⋮ → Open in Chrome.' : 'Tap ••• at the top, then “Open in external browser”.'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {isAndroid() && (
          <a href={androidIntentUrl(href)} onClick={() => landingBeacon('click', 0, '/open-in-browser')} className={`inline-flex h-11 items-center rounded-full px-5 text-[15px] font-semibold ${btn}`} data-testid="open-in-browser-android">
            Open in Chrome
          </a>
        )}
        {isIOS() && (
          <a href={safariUrl(href)} onClick={() => landingBeacon('click', 0, '/open-in-browser')} className={`inline-flex h-11 items-center rounded-full px-5 text-[15px] font-semibold ${btn}`} data-testid="open-in-browser-ios">
            Open in Safari
          </a>
        )}
        <button type="button" onClick={() => void copy()} className={`inline-flex h-11 items-center rounded-full px-5 text-[15px] font-semibold ${ghost}`} data-testid="open-in-browser-copy">
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}
